/**
 * LOS DATOS QUE EL POS NECESITA PARA VENDER — con o sin internet.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  CÓMO FUNCIONA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *  1. Al abrir, lee **primero la copia del dispositivo**. La grilla aparece al
 *     instante, incluso sin señal.
 *  2. Si hay internet, descarga la versión fresca y reemplaza la copia.
 *  3. Si no hay internet, se queda con la copia y avisa de cuándo es.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  DECISIÓN: LA BÚSQUEDA SIEMPRE ES LOCAL
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El POS filtra sobre los datos del dispositivo, nunca pidiéndole al servidor.
 * Dos razones:
 *
 *   · **Velocidad.** Escanear un código y esperar 300 ms a que responda el
 *     servidor se siente lento cuando hay cola. Buscando en memoria es instantáneo.
 *   · **Un solo comportamiento.** Si con internet buscara en el servidor y sin
 *     internet en el dispositivo, el cajero vería resultados distintos según la
 *     señal — y eso desconcierta.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  asegurarNegocio,
  asegurarSucursal,
  guardarFoto,
  guardarHuellas,
  guardarMonedas,
  guardarProductos,
  leerFoto,
  leerHuellas,
} from "@/lib/db";
import {
  catalogo,
  metodosPago,
  monedas as monedasApi,
} from "@/lib/endpoints";
import type { ItemListRow, PaymentMethod, TenantCurrency } from "@/lib/types";
import { ponerAlDiaLosClientes } from "./clientesDelAparato";
import { descargarPaginadoConTope } from "./descargarPaginado";
import { ordenarCatalogo, renovarPorHuellas, type ResultadoPorHuellas } from "./porHuellas";
import { restarLoVendido } from "./existencia";
import { ACCESO_ACTUALIZADO, useAuth } from "@/stores/auth";
import { useConexion } from "@/stores/conexion";
import { useSucursal } from "@/stores/sucursal";

/** Cuántos productos por petición al traer el catálogo. */
const POR_PAGINA = 200;

/**
 * Tope de productos que se guardan en el dispositivo.
 *
 * Con 10.000 productos la búsqueda en memoria sigue siendo instantánea y el
 * almacenamiento del navegador no sufre. Un supermercado con más que eso
 * necesitaría búsqueda por servidor con respaldo local — es una mejora conocida,
 * no un caso de los negocios a los que apunta Softfi hoy.
 *
 * ⚠️ Eran 5.000 y el corte era mudo: el negocio que se pasaba veía un catálogo
 * incompleto sin ningún aviso. Lo encontró la auditoría. Ahora se sube, y si
 * aun así no cabe, la caja lo dice (`catalogoRecortado`).
 */
export const TOPE_PRODUCTOS = 10000;

export type DatosPos = {
  items: ItemListRow[];
  monedas: TenantCurrency[];
  metodos: PaymentMethod[];

  cargando: boolean;
  /** true cuando lo que se muestra viene del dispositivo, no del servidor. */
  desdeCache: boolean;
  /**
   * true mientras se pide lo fresco al servidor. Con la copia ya pintada,
   * `desdeCache` es true en ese segundo aunque haya internet: no es un aviso
   * de «sin conexión». Ver el aviso de `PosPage`.
   */
  renovando: boolean;
  actualizadoEn: string | null;
  /**
   * true si no hay datos guardados y tampoco se pudieron traer: no se puede
   * vender en este dispositivo.
   *
   * ⚠️ NO es lo mismo que "el catálogo está vacío". Un negocio recién creado
   * tiene cero productos y está perfectamente conectado: lo que le hace falta es
   * cargar mercancía, no internet. Ver el comentario de `descargaOk` abajo.
   */
  sinDatos: boolean;
  /**
   * `true` si el catálogo tiene más productos de los que caben en el aparato
   * (`TOPE_PRODUCTOS`). Solo se sabe al descargar; de la copia guardada, no.
   */
  catalogoRecortado: boolean;

  refrescar: () => Promise<void>;
  /** Vuelve a pedir SOLO las tasas. Ver `refrescarTasas`. */
  refrescarTasas: () => Promise<void>;
  /**
   * Le resta a la esquina de cada tarjeta lo que se acaba de vender. Ver
   * `restarLoVendido`.
   */
  descontarVendido: (lineas: readonly { itemId: string; cantidad: number }[]) => void;
};

/** Cada cuánto se vuelven a pedir las tasas con la Caja abierta: 5 minutos. */
const CADA_CUANTO_LAS_TASAS = 5 * 60 * 1000;

export function useDatosPos(): DatosPos {
  const tenantId = useAuth((s) => s.tenant?.id);
  const enLinea = useConexion((s) => s.enLinea);
  /*
    La sucursal de este aparato, para que la existencia sea la de aquí.

    ⚠️ Está en las dependencias de la descarga: si se cambia de sucursal, el
    catálogo se vuelve a bajar. Si no, la esquina seguiría enseñando lo que hay
    en el otro local — y el cajero cobraría algo que no tiene.
  */
  const sucursal = useSucursal((s) => (tenantId ? (s.porNegocio[tenantId] ?? null) : null));

  const [items, setItems] = useState<ItemListRow[]>([]);
  const [monedas, setMonedas] = useState<TenantCurrency[]>([]);
  const [metodos, setMetodos] = useState<PaymentMethod[]>([]);
  const [cargando, setCargando] = useState(true);
  const [desdeCache, setDesdeCache] = useState(false);
  const [renovando, setRenovando] = useState(false);
  const [actualizadoEn, setActualizadoEn] = useState<string | null>(null);
  const [catalogoRecortado, setCatalogoRecortado] = useState(false);

  /**
   * ¿La última descarga terminó bien?
   *
   * Existe por un fallo concreto: al crear un segundo negocio, el POS enseñaba
   * "No hay catálogo guardado en este dispositivo · conéctate a internet" con su
   * icono de wifi tachado. No había ningún problema de conexión — el negocio
   * acababa de nacer y no tenía productos.
   *
   * Ese mensaje manda a la persona a revisar el router en vez de a cargar su
   * mercancía, que es lo que de verdad tiene que hacer. Distinguir "no pude
   * traerlo" de "no hay nada que traer" es lo único que arregla eso.
   */
  const [descargaOk, setDescargaOk] = useState(false);

  /**
   * Trae el catálogo completo.
   *
   * ═══════════════════════════════════════════════════════════════════════════
   *  POR QUÉ LAS PÁGINAS VAN EN PARALELO Y NO EN FILA INDIA
   * ═══════════════════════════════════════════════════════════════════════════
   *
   * Antes esto pedía la página 1, **esperaba**, pedía la 2, esperaba… Con los
   * 3.470 productos de un negocio real son 18 viajes seguidos al servidor. Si
   * cada uno tarda 200 ms, son casi cuatro segundos, y durante esos cuatro
   * segundos el punto de venta no sirve: la primera vez que alguien abre Softfi
   * en un aparato nuevo, o cuando se limpia la copia guardada.
   *
   * Y lo peor es que esperar no aportaba nada. La página 5 no depende de la 4:
   * lo único que hace falta saber antes es CUÁNTAS hay, y eso lo dice la
   * primera respuesta. Así que se pide la 1, se lee cuántas son, y el resto
   * van juntas.
   */
  const usuarioId = useAuth((s) => s.user?.id ?? "");

  const renovarCatalogo = useCallback(async (): Promise<ResultadoPorHuellas<ItemListRow>> => {
    const filtros = {
      only_pos: true,
      only_active: true,
      // Cuánto hay de cada uno, para la esquina de la tarjeta.
      con_existencia: true,
      // La caja y la tira de cada producto: para el «¿suelto o caja?» y
      // para que el lector encuentre el código de la caja sin internet.
      con_presentaciones: true,
      ...(sucursal ? { location_id: sucursal } : {}),
    };
    const pedirPagina = (pagina: number, grupos?: string) =>
      catalogo.listar({ ...filtros, ...(grupos ? { grupos } : {}), page: pagina, size: POR_PAGINA });

    try {
      /*
        ⚠️ SOLO LO QUE CAMBIÓ. Antes se bajaba el catálogo entero cada vez
        que se abría Vender. Ver `porHuellas.ts`.

        La clave lleva la sucursal —la existencia es la de aquí— y quién la
        baja —el costo solo le llega a quien puede verlo—: si cambia
        cualquiera de las dos, la copia de antes no sirve y se baja todo.
      */
      return await renovarPorHuellas<ItemListRow>({
        pedirHuellas: () => catalogo.huellas(filtros),
        pedirPagina,
        locales: (await leerFoto()).items,
        guardadas: await leerHuellas("catalogo"),
        clave: `v1|${tenantId}|${sucursal ?? ""}|${usuarioId}`,
        porPagina: POR_PAGINA,
        tope: TOPE_PRODUCTOS,
      });
    } catch {
      // Un servidor sin huellas —el de antes, mientras se actualiza—, o una
      // huella que no se pudo leer: como siempre, todo entero.
      const { items: filas, recortado } = await descargarPaginadoConTope(
        (pagina) => pedirPagina(pagina),
        { porPagina: POR_PAGINA, tope: TOPE_PRODUCTOS },
      );
      return { filas, huellas: null, recortado, bajadas: filas.length };
    }
  }, [sucursal, tenantId, usuarioId]);


  /*
    ⚠️ SOLO LA ÚLTIMA DESCARGA PINTA Y GUARDA (2026-09-29).

    Con 3.400 productos, bajar el catálogo tarda unos segundos. Si en ese
    rato se cambiaba de sucursal, las dos descargas iban a la vez y la vieja,
    si terminaba de última, pisaba a la nueva: la caja enseñaba —y guardaba
    para vender sin internet— el catálogo de la sucursal de antes. Cada
    descarga lleva su número; si al terminar ya no es la última, se descarta.
  */
  const vuelta = useRef(0);

  const cargar = useCallback(
    async (forzarRed: boolean) => {
      if (!tenantId) return;
      const esta = ++vuelta.current;
      const sigueSiendoLaUltima = () => vuelta.current === esta;

      // Si el usuario cambió de negocio, se borra la copia para no mezclar
      // catálogos de dos negocios distintos. Y si cambió de sucursal, la del
      // catálogo y los métodos de pago (ver `asegurarSucursal`).
      await asegurarNegocio(tenantId);
      await asegurarSucursal(sucursal);
      if (!sigueSiendoLaUltima()) return;

      // --- Paso 1: lo guardado, para pintar de inmediato ---
      const copia = await leerFoto();
      if (!sigueSiendoLaUltima()) return;
      if (copia.items.length > 0) {
        // ⚠️ Ordenada como la del servidor: el aparato la devuelve por
        // identificador, y la caja abría con los productos revueltos
        // («c30215285 morral…» de primero) hasta que llegaba la buena.
        setItems(ordenarCatalogo(copia.items));
        setMonedas(copia.monedas);
        setMetodos(copia.metodos);
        setActualizadoEn(copia.actualizadoEn);
        setDesdeCache(true);
        setCargando(false);
      }

      // --- Paso 2: lo fresco, si hay internet ---
      if (!enLinea && !forzarRed) {
        setCargando(false);
        return;
      }

      setRenovando(true);
      try {
        /*
          ⚠️ LOS CLIENTES VAN APARTE Y PUEDEN FALLAR SIN ARRASTRAR AL RESTO.

          `Promise.all` se cae entera si UNA falla. Metiendo los clientes
          dentro, un negocio cuyo listado de clientes diera error —o un cajero
          sin permiso para verlos— se quedaría también sin catálogo: no podría
          vender NADA por no poder bajar una lista que a lo mejor ni usa.

          Así que se piden por su lado y, si no llegan, la lista de clientes
          que ya hubiera se queda intacta.

          ⚠️ Y SE GUARDAN APARTE, CUANDO TERMINEN. Ya no tienen tope: en un
          negocio con decenas de miles tardan más que el catálogo, y antes la
          caja los esperaba antes de pedir el catálogo siquiera. Ahora los dos
          salen a la vez, el catálogo se guarda en cuanto llega, y los clientes
          cuando lleguen ellos.
        */
        // Ver `clientesDelAparato.ts`: los guarda y repinta el selector solo.
        void ponerAlDiaLosClientes(tenantId);

        const [catalogoBajado, listaMonedas, listaMetodos] = await Promise.all([
          renovarCatalogo(),
          monedasApi.listar(),
          metodosPago.listar(true),
        ]);
        // Otra descarga más nueva manda: esta es de antes (otra sucursal).
        if (!sigueSiendoLaUltima()) return;
        const listaItems = ordenarCatalogo(catalogoBajado.filas);

        await guardarFoto({
          items: listaItems,
          monedas: listaMonedas,
          metodos: listaMetodos,
        });
        // Las huellas, DESPUÉS de guardar lo que dicen: si algo falla a medias,
        // la próxima vez no coinciden y se vuelve a bajar lo que faltó.
        await guardarHuellas("catalogo", catalogoBajado.huellas);

        setItems(listaItems);
        setMonedas(listaMonedas);
        setMetodos(listaMetodos);
        setCatalogoRecortado(catalogoBajado.recortado);
        setDesdeCache(false);
        setActualizadoEn(new Date().toISOString());
        setDescargaOk(true);
      } catch {
        // Falló la descarga (se cayó el internet a mitad). Nos quedamos con la
        // copia que ya cargamos en el paso 1: es vieja, pero permite vender.
        if (sigueSiendoLaUltima()) setDesdeCache(true);
      } finally {
        if (sigueSiendoLaUltima()) {
          setCargando(false);
          setRenovando(false);
        }
      }
    },
    [tenantId, sucursal, enLinea, renovarCatalogo],
  );

  // ── Al cambiar de negocio o de sucursal, borrón y cuenta nueva ───────────
  //
  // Sin esto, `descargaOk` se quedaría en `true` desde el negocio anterior y el
  // POS diría que ya tiene el catálogo del nuevo antes de haberlo pedido. Los
  // productos también se vacían para que no se vean los del otro local ni un
  // instante mientras carga. Con la sucursal pasa lo mismo: sus precios, su
  // existencia y sus métodos de pago son de ella.
  useEffect(() => {
    setDescargaOk(false);
    setItems([]);
    setMetodos([]);
    setCatalogoRecortado(false);
    setCargando(true);
  }, [tenantId, sucursal]);

  useEffect(() => {
    void cargar(false);
    // Al cambiar de sucursal o salir de la pantalla, la que iba en camino ya
    // no es la última: no pinta ni guarda nada.
    return () => {
      vuelta.current++;
    };
  }, [cargar]);

  // Si le dan o le quitan permisos (o sucursales) con la Caja abierta, vuelve
  // a bajar lo suyo: a un cajero al que le acaban de dar los clientes le tienen
  // que salir ya. Ver `actualizarAcceso` en `stores/auth.ts`.
  useEffect(() => {
    const alCambiar = () => void cargar(true);
    window.addEventListener(ACCESO_ACTUALIZADO, alCambiar);
    return () => window.removeEventListener(ACCESO_ACTUALIZADO, alCambiar);
  }, [cargar]);

  const descontarVendido = useCallback(
    (lineas: readonly { itemId: string; cantidad: number }[]) => {
      const { items: nuevos, cambiados } = restarLoVendido(items, lineas);
      if (cambiados.length === 0) return;
      setItems(nuevos);
      // Si no se puede guardar, la pantalla ya está bien: solo se perdería al
      // recargar sin señal. No vale un error en mitad de una venta.
      void guardarProductos(cambiados).catch(() => undefined);
    },
    [items],
  );

  /*
    ═══════════════════════════════════════════════════════════════════════════
     ⚠️ LA TASA SE REFRESCA SOLA MIENTRAS LA CAJA ESTÁ ABIERTA
    ═══════════════════════════════════════════════════════════════════════════

    Las monedas se bajaban una vez, al abrir la Caja. Y en una bodega la Caja
    se abre en la mañana y no se cierra: cuando la tasa cambiaba —a medianoche,
    o en la tarde con «Usar la de mañana» del BCV— la pantalla seguía
    calculando los bolívares con la vieja. El cliente pagaba lo que decía la
    pantalla y el servidor, que convierte con la de HOY, contestaba «el pago
    no cubre el total» (o «sobran»), con la gente esperando y sin que el
    cajero pudiera entender por qué.

    Así que se vuelven a pedir cada pocos minutos, al volver a la pestaña y al
    tocar «Cobrar» (ver `alCobrar` en `PosPage`). Son unas pocas monedas: no
    cuesta nada. Si no cambió ninguna tasa, no se toca el estado — así el
    cobro no se vuelve a pintar por nada.
  */
  const refrescarTasas = useCallback(async () => {
    if (!tenantId || !enLinea) return;
    try {
      const lista = await monedasApi.listar();
      const firma = (ms: TenantCurrency[]) =>
        JSON.stringify(ms.map((m) => [m.currency_code, m.current_rate, m.is_active]));
      setMonedas((antes) => (firma(antes) === firma(lista) ? antes : lista));
      await guardarMonedas(lista);
    } catch {
      // Sin red o el servidor no contesta: se sigue con la que había.
    }
  }, [tenantId, enLinea]);

  useEffect(() => {
    if (!enLinea) return;
    const cada = window.setInterval(() => void refrescarTasas(), CADA_CUANTO_LAS_TASAS);
    const alVolver = () => {
      if (document.visibilityState === "visible") void refrescarTasas();
    };
    document.addEventListener("visibilitychange", alVolver);
    return () => {
      window.clearInterval(cada);
      document.removeEventListener("visibilitychange", alVolver);
    };
  }, [enLinea, refrescarTasas]);

  return {
    items,
    monedas,
    metodos,
    cargando,
    desdeCache,
    renovando,
    actualizadoEn,
    sinDatos: !cargando && items.length === 0 && !descargaOk,
    catalogoRecortado,
    refrescar: () => cargar(true),
    refrescarTasas,
    descontarVendido,
  };
}
